import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import DreamieTransition, {
  DreamieTransitionHandle,
} from './DreamieTransition';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetAssistantCandidateResponse,
  GetMyAssistantResponse,
  GetAgentImageResponse,
  UpdateAgentNicknameResponse,
  RegenerateAssistantImageResponse,
  FinalizeAssistantResponse,
  ImageRunPreviewEvent,
  ImageRunCompleteEvent,
  ImageRunFailedEvent,
} from '@dreamai/types';
import api from '../../../api';
import { OnboardingStepProps } from '../OnboardingFlow';
import { usePusher } from '../../../contexts/PusherContext';
import './meet-assistant-step.scss';

const MAX_APPEARANCE_CHANGES = 100;
const WARNING_THRESHOLD = Math.floor(MAX_APPEARANCE_CHANGES * 0.9);

interface ChatMsg {
  role: 'agent' | 'user';
  text: string;
}
interface ImageEntry {
  url: string;
  predominantColour: string | null;
  runId?: string;
}

interface StoredCandidate {
  candidateId: string;
  agentId: string;
  profile: {
    name: string;
    nickname?: string | null;
    backstory?: string | null;
    introMessages?: string[];
    funFacts?: string[];
  };
  imageUrl: string | null;
  imageReady: boolean;
  predominantColour?: string | null;
}

function bubbleColors(hex: string | null) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex))
    return { border: '#c1c5cd', from: '#f5f3ef', to: '#faf9f7' };
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number, w: number) => Math.round(c + (255 - c) * w);
  const darken = (c: number) => Math.round(c * 0.45);
  const toHex = (...rgb: number[]) =>
    '#' + rgb.map((c) => c.toString(16).padStart(2, '0')).join('');
  return {
    border: toHex(darken(r), darken(g), darken(b)),
    from: toHex(mix(r, 0.55), mix(g, 0.55), mix(b, 0.55)),
    to: toHex(mix(r, 0.88), mix(g, 0.88), mix(b, 0.88)),
  };
}

const FaceContentIcon = () => (
  <svg viewBox='0 0 20 20' fill='none'>
    <circle cx='10' cy='10' r='8' stroke='currentColor' strokeWidth='1.5' />
    <circle cx='7' cy='8.5' r='1' fill='currentColor' />
    <circle cx='13' cy='8.5' r='1' fill='currentColor' />
    <path
      d='M7 13c1 1.5 5 1.5 6 0'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
);
const ArrowCircleRightIcon = () => (
  <svg viewBox='0 0 20 20' fill='none'>
    <circle cx='10' cy='10' r='7.5' stroke='currentColor' strokeWidth='1.5' />
    <path
      d='M10 6.67l3.33 3.33-3.33 3.33M6.67 10h6.66'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg viewBox='0 0 20 20' fill='none'>
    <path
      d='M12.5 5l-5 5 5 5'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox='0 0 20 20' fill='none'>
    <path
      d='M7.5 5l5 5-5 5'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const SendIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M12 19V5M5 12l7-7 7 7' />
  </svg>
);
const EditIcon = () => (
  <svg viewBox='0 0 24 24' fill='none'>
    <path
      d='M12 20.0002H21M3 20.0002H4.67454C5.16372 20.0002 5.40832 20.0002 5.63849 19.945C5.84256 19.896 6.03765 19.8152 6.2166 19.7055C6.41843 19.5818 6.59138 19.4089 6.93729 19.063L19.5 6.50023C20.3285 5.6718 20.3285 4.32865 19.5 3.50023C18.6716 2.6718 17.3285 2.6718 16.5 3.50023L3.93726 16.063C3.59136 16.4089 3.4184 16.5818 3.29472 16.7837C3.18506 16.9626 3.10425 17.1577 3.05526 17.3618C3 17.5919 3 17.8365 3 18.3257V20.0002Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
const FaceWinkIcon = () => (
  <svg viewBox='0 0 20 20' fill='none'>
    <circle cx='10' cy='10' r='8' stroke='currentColor' strokeWidth='1.5' />
    <circle cx='7' cy='8.5' r='1' fill='currentColor' />
    <path
      d='M12.5 8c.28 0 .5.22.5.5s-.22.5-.5.5-.5-.22-.5-.5.22-.5.5-.5Z'
      fill='currentColor'
    />
    <path
      d='M13 8.5c-.3-.5-1-.5-1 0'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
    />
    <path
      d='M7 13c1 1.5 5 1.5 6 0'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
);

type Phase = 'loading' | 'idle' | 'regenerating' | 'finishing';

interface MeetAssistantStepProps extends OnboardingStepProps {
  onSetupStarted?: (workflowId: string | null) => void;
}

const MeetAssistantStep: FC<MeetAssistantStepProps> = ({
  refreshStatus,
  onSetupStarted,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [agentId, setAgentId] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [userName, setUserName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [images, setImages] = useState<ImageEntry[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [phase, setPhase] = useState<Phase>('loading');
  const [inputText, setInputText] = useState('');
  const [appearanceChanges, setAppearanceChanges] = useState(0);

  // View state: intro (landing) vs edit (change name + appearance)
  const [view, setView] = useState<'intro' | 'edit'>('intro');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [localNickname, setLocalNickname] = useState('');
  const [funFacts, setFunFacts] = useState<string[]>([]);
  const [currentFact, setCurrentFact] = useState('');
  const factQueueRef = useRef<string[]>([]);
  const usedFactsRef = useRef<Set<string>>(new Set());

  // Snapshot for cancel: revert to this state when user cancels edit
  const snapshotRef = useRef<{ nickname: string; imageIndex: number } | null>(
    null,
  );

  // Pusher subscription for real-time image regeneration events
  const pusherCtx = usePusher(true) as any;
  const userChannel = pusherCtx?.userChannel;
  const pendingRunIdRef = useRef<string | null>(null);
  const transitionRef = useRef<DreamieTransitionHandle>(null);
  const pendingPreviewRef = useRef<{
    entry: ImageEntry;
    newIdx: number;
  } | null>(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const currentImageIndexRef = useRef(currentImageIndex);
  currentImageIndexRef.current = currentImageIndex;

  // ── Rotating fun facts during regeneration ─────────────────────────
  useEffect(() => {
    if (phase !== 'regenerating' || funFacts.length === 0) {
      setCurrentFact('');
      return;
    }

    // Build a queue from unused facts. If all used, or none with a
    // nickname template remain, reset so the first fact always has a name.
    let available = funFacts.filter((f) => !usedFactsRef.current.has(f));
    const hasTemplate = available.some((f) => /\{\{[^}]*\}\}/.test(f));
    if (available.length === 0 || !hasTemplate) {
      usedFactsRef.current.clear();
      available = [...funFacts];
    }

    // First fact must contain {{...}} (nickname template) for context
    const withTemplate = available.filter((f) => /\{\{[^}]*\}\}/.test(f));
    const withoutTemplate = available.filter((f) => !/\{\{[^}]*\}\}/.test(f));
    const shuffledWith = [...withTemplate].sort(() => Math.random() - 0.5);
    const shuffledWithout = [...withoutTemplate].sort(
      () => Math.random() - 0.5,
    );
    const first = shuffledWith.shift();
    const rest = [...shuffledWith, ...shuffledWithout].sort(
      () => Math.random() - 0.5,
    );
    factQueueRef.current = first ? [first, ...rest] : rest;

    const showNext = () => {
      const queue = factQueueRef.current;
      if (queue.length === 0) return;
      const fact = queue.shift()!;
      usedFactsRef.current.add(fact);
      const n = localNickname || nickname;
      setCurrentFact(fact.replace(/\{\{[^}]*\}\}/g, n));

      // If queue is exhausted mid-transition, refill from all facts (reset)
      if (queue.length === 0) {
        usedFactsRef.current.clear();
        const refill = [...funFacts].sort(() => Math.random() - 0.5);
        factQueueRef.current = refill;
      }
    };

    showNext();
    const interval = setInterval(showNext, 5000);
    return () => clearInterval(interval);
  }, [phase, funFacts, localNickname, nickname]);

  const canChangeAppearance = appearanceChanges < MAX_APPEARANCE_CHANGES;
  const currentColour = images[currentImageIndex]?.predominantColour || null;
  const colors = bubbleColors(currentColour);

  const introMessages: ChatMsg[] = [
    {
      role: 'agent',
      text: `Hi ${userName}, I'm your new Personal Assistant, ${nickname}.\nI'm here to help make your life easier.`,
    },
    {
      role: 'agent',
      text: 'Before we start would you like to change anything about me? You can change my appearance and name.',
    },
  ];

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Pusher events for image regeneration ───────────────────────────
  useEffect(() => {
    if (!userChannel) {
      console.log('[MeetAssistant] Pusher userChannel not available yet');
      return;
    }
    console.log(
      '[MeetAssistant] Binding Pusher image-run handlers on userChannel',
    );

    const handlePreview = (data: ImageRunPreviewEvent) => {
      console.log(
        '[MeetAssistant] Received image-run-preview event:',
        data.runId,
        'pending:',
        pendingRunIdRef.current,
      );
      if (data.runId !== pendingRunIdRef.current) return;

      const newEntry: ImageEntry = {
        url: data.previewUrl,
        predominantColour: data.predominantColour,
        runId: data.runId,
      };

      const newIdx = imagesRef.current.length;

      // Store the pending preview so onReformComplete can finalize it
      pendingPreviewRef.current = { entry: newEntry, newIdx };

      // Trigger the reform animation — the canvas will reassemble
      // into the new image. UI state updates happen in onReformComplete.
      transitionRef.current?.reformTo(data.previewUrl);
    };

    const handleComplete = (data: ImageRunCompleteEvent) => {
      console.log(
        '[MeetAssistant] Received image-run-complete event:',
        data.runId,
      );
      // During onboarding we always display the white-bg preview (looks
      // native on the white UI).  The transparent image is stored in the
      // DB but not shown here.  A high-quality OpenAI transparent version
      // is created asynchronously when the user finalizes via /complete.
    };

    const handleFailed = (data: ImageRunFailedEvent) => {
      console.log(
        '[MeetAssistant] Received image-run-failed event:',
        data.runId,
        'pending:',
        pendingRunIdRef.current,
      );
      if (data.runId !== pendingRunIdRef.current) return;
      pendingRunIdRef.current = null;
      setError(data.error || 'Image generation failed. Please try again.');
      // If the failure doesn't count against the limit (service outage),
      // give the attempt back so the user isn't penalised.
      if (data.countsAgainstLimit === false) {
        setAppearanceChanges((prev) => Math.max(0, prev - 1));
      }
      // Revert the particle animation back to the previous image
      const prevUrl = imagesRef.current[currentImageIndexRef.current]?.url;
      if (prevUrl) {
        transitionRef.current?.reformTo(prevUrl);
      }
      setPhase((current) => (current === 'regenerating' ? 'idle' : current));
    };

    userChannel.bind('image-run-preview', handlePreview);
    userChannel.bind('image-run-complete', handleComplete);
    userChannel.bind('image-run-failed', handleFailed);

    return () => {
      userChannel.unbind('image-run-preview', handlePreview);
      userChannel.unbind('image-run-complete', handleComplete);
      userChannel.unbind('image-run-failed', handleFailed);
    };
  }, [userChannel]);

  // ── Load agent data on mount ─────────────────────────────────────────
  useEffect(() => {
    const stored = sessionStorage.getItem('onboarding_candidate');
    const storedUserName = sessionStorage.getItem('onboarding_userName') || '';

    if (stored) {
      let data: StoredCandidate;
      try {
        data = JSON.parse(stored);
      } catch {
        return;
      }

      setAgentId(data.agentId);
      const nick =
        data.profile.nickname || data.profile.name || 'your assistant';
      setNickname(nick);
      setUserName(storedUserName);
      if (data.profile.funFacts?.length) setFunFacts(data.profile.funFacts);
      if ((data as any).imageRegenerationCount != null)
        setAppearanceChanges((data as any).imageRegenerationCount);

      if (data.imageReady && data.imageUrl) {
        setImages([
          {
            url: data.imageUrl,
            predominantColour: data.predominantColour || null,
          },
        ]);
        setPhase('idle');
      } else {
        const pollCandidate = async () => {
          try {
            const token = await getAccessTokenSilently();
            const res = await api.get<GetAssistantCandidateResponse>(
              `/assistant-onboarding/candidate/${data.candidateId}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (res.data.imageUrl) {
              setImages([
                {
                  url: res.data.imageUrl,
                  predominantColour: data.predominantColour || null,
                },
              ]);
              setPhase('idle');
              if (pollRef.current) clearInterval(pollRef.current);
            }
          } catch {
            /* retry */
          }
        };
        pollCandidate();
        pollRef.current = setInterval(pollCandidate, 3000);
      }
    } else {
      const fetchMyAssistant = async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await api.get<GetMyAssistantResponse>(
            '/assistant-onboarding/my-assistant',
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setAgentId(res.data.agentId);
          const nick =
            res.data.profile.nickname ||
            res.data.profile.name ||
            'your assistant';
          setNickname(nick);
          const uName = (res.data as any).userName || storedUserName;
          setUserName(uName);
          if ((res.data as any).profile.funFacts?.length)
            setFunFacts((res.data as any).profile.funFacts);
          if (res.data.imageRegenerationCount != null)
            setAppearanceChanges(res.data.imageRegenerationCount);

          if (res.data.imageReady && res.data.imageUrl) {
            setImages([
              {
                url: res.data.imageUrl,
                predominantColour: (res.data as any).predominantColour || null,
              },
            ]);
            setPhase('idle');
          } else {
            const pollAgentImg = async () => {
              try {
                const t = await getAccessTokenSilently();
                const imgRes = await api.get<GetAgentImageResponse>(
                  `/assistant-onboarding/agent/${res.data.agentId}/image`,
                  { headers: { Authorization: `Bearer ${t}` } },
                );
                if (imgRes.data.imageUrl) {
                  setImages([
                    {
                      url: imgRes.data.imageUrl,
                      predominantColour: imgRes.data.predominantColour || null,
                    },
                  ]);
                  setPhase('idle');
                  if (pollRef.current) clearInterval(pollRef.current);
                }
              } catch {
                /* retry */
              }
            };
            pollAgentImg();
            pollRef.current = setInterval(pollAgentImg, 3000);
          }
        } catch {
          setError('Could not load your assistant. Please try refreshing.');
        }
      };
      fetchMyAssistant();
    }
  }, [getAccessTokenSilently]);

  // ── Action handlers ──────────────────────────────────────────────────

  const handleEnterEdit = useCallback(() => {
    snapshotRef.current = { nickname, imageIndex: currentImageIndex };
    setLocalNickname(nickname);
    setView('edit');
    setEditingName(false);
    setInputText('');
    setError(null);
  }, [nickname, currentImageIndex]);

  const handleEditCancel = useCallback(() => {
    const snap = snapshotRef.current;
    if (snap) {
      setNickname(snap.nickname);
      setCurrentImageIndex(snap.imageIndex);
    }
    snapshotRef.current = null;
    setView('intro');
    setEditingName(false);
    setInputText('');
    setError(null);
  }, []);

  const handleSaveAssistant = useCallback(async () => {
    if (!agentId) return;
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      // Persist nickname if it changed
      if (localNickname !== snapshotRef.current?.nickname) {
        await api.patch<UpdateAgentNicknameResponse>(
          `/assistant-onboarding/agent/${agentId}/nickname`,
          { nickname: localNickname },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      setNickname(localNickname);
      snapshotRef.current = null;
      setView('intro');
      setEditingName(false);
      setInputText('');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save changes.');
    }
  }, [agentId, localNickname, getAccessTokenSilently]);

  const handleStartEditingName = useCallback(() => {
    setNameInput(localNickname);
    setEditingName(true);
  }, [localNickname]);

  const handleSaveNameLocally = useCallback(() => {
    if (nameInput.trim()) {
      setLocalNickname(nameInput.trim());
    }
    setEditingName(false);
  }, [nameInput]);

  const handleReformComplete = useCallback(() => {
    const pending = pendingPreviewRef.current;
    if (!pending) return;
    pendingPreviewRef.current = null;

    setImages((prev) => [...prev, pending.entry]);
    setCurrentImageIndex(pending.newIdx);
    setAppearanceChanges((prev) => prev + 1);
    setPhase('idle');
  }, []);

  const handleSubmitAppearance = useCallback(async () => {
    if (!agentId || !inputText.trim()) return;
    const fb = inputText.trim();

    setPhase('regenerating');
    setInputText('');
    setError(null);

    // Kick off the dissolve → hold particle animation
    transitionRef.current?.dissolve();

    try {
      const token = await getAccessTokenSilently();
      const regenRes = await api.post<RegenerateAssistantImageResponse>(
        '/assistant-onboarding/regenerate-image',
        {
          agentId,
          feedback: fb,
          baseRunId: images[currentImageIndex]?.runId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Track the pending run — Pusher events will handle UI updates
      pendingRunIdRef.current = regenRes.data.runId;

      // Sync appearance counter from the authoritative backend value
      if (regenRes.data.remainingChanges != null) {
        setAppearanceChanges(
          MAX_APPEARANCE_CHANGES - regenRes.data.remainingChanges,
        );
      }

      // Hard timeout: if no Pusher event arrives within 2.5 minutes
      setTimeout(
        () => {
          if (pendingRunIdRef.current === regenRes.data.runId) {
            pendingRunIdRef.current = null;
            // Revert the particle animation back to the previous image
            const prevUrl =
              imagesRef.current[currentImageIndexRef.current]?.url;
            if (prevUrl) {
              transitionRef.current?.reformTo(prevUrl);
            }
            setPhase((current) => {
              if (current === 'regenerating') {
                setError('Image generation timed out. Please try again.');
                return 'idle';
              }
              return current;
            });
          }
        },
        2.5 * 60 * 1000,
      );
    } catch (err: any) {
      // Revert the particle animation back to the previous image
      const prevUrl = imagesRef.current[currentImageIndexRef.current]?.url;
      if (prevUrl) {
        transitionRef.current?.reformTo(prevUrl);
      }
      setError(err?.response?.data?.error || 'Failed to regenerate image.');
      setPhase('idle');
    }
  }, [agentId, inputText, getAccessTokenSilently, images, currentImageIndex]);

  const handleStartWorking = useCallback(async () => {
    setPhase('finishing');
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.post<FinalizeAssistantResponse>(
        '/assistant-onboarding/finalize-assistant',
        {
          agentId,
          selectedRunId: images[currentImageIndex]?.runId,
          nickname,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Save the transparent assistant image (from agents.image_url) for the setup loading screen
      if (res.data.imageUrl) {
        sessionStorage.setItem('onboarding_assistant_image', res.data.imageUrl);
      }
      sessionStorage.removeItem('onboarding_personality');
      sessionStorage.removeItem('onboarding_candidate');
      sessionStorage.removeItem('onboarding_userName');
      // Pass the workflow ID to the parent so AssistantSetupStep can track it
      if (onSetupStarted) {
        onSetupStarted(res.data.workflowId || null);
      }
      await refreshStatus();
    } catch {
      setError('Something went wrong. Please try again.');
      setPhase('idle');
    }
  }, [
    agentId,
    images,
    currentImageIndex,
    nickname,
    getAccessTokenSilently,
    refreshStatus,
    onSetupStarted,
  ]);

  const goImageLeft = () => {
    if (currentImageIndex > 0) setCurrentImageIndex((i) => i - 1);
  };
  const goImageRight = () => {
    if (currentImageIndex < images.length - 1)
      setCurrentImageIndex((i) => i + 1);
  };

  const hasMultipleImages = images.length > 1;
  const isRegenerating = phase === 'regenerating';

  // ── Loading state ────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className='onboarding-step meet-assistant-step'>
        <div className='assistant-loading'>
          <div className='image-spinner' />
          <p>Getting your assistant ready…</p>
        </div>
      </div>
    );
  }

  const currentImage = images[currentImageIndex];

  // ── Edit view (change name + appearance) ─────────────────────────────
  if (view === 'edit') {
    return (
      <div className='onboarding-step meet-assistant-step'>
        <div className='edit-content'>
          <div className='edit-image-section'>
            {currentImage ? (
              <div className='image-wrapper'>
                <DreamieTransition
                  ref={transitionRef}
                  imageUrl={currentImage.url}
                  alt={localNickname}
                  onReformComplete={handleReformComplete}
                  width={293}
                  height={440}
                />
              </div>
            ) : (
              <div className='image-placeholder' />
            )}

            {hasMultipleImages && (
              <div className='image-picker'>
                <button
                  className={`picker-arrow ${currentImageIndex === 0 ? 'disabled' : ''}`}
                  onClick={goImageLeft}
                  disabled={currentImageIndex === 0 || isRegenerating}
                >
                  <ChevronLeftIcon />
                </button>
                <div className='picker-dots'>
                  {images.map((_, i) => (
                    <span
                      key={i}
                      className={`dot ${i === currentImageIndex ? 'active' : ''} ${!canChangeAppearance && i === images.length - 1 ? 'limit' : ''}`}
                    />
                  ))}
                </div>
                <button
                  className={`picker-arrow ${currentImageIndex >= images.length - 1 ? 'disabled' : ''}`}
                  onClick={goImageRight}
                  disabled={
                    currentImageIndex >= images.length - 1 || isRegenerating
                  }
                >
                  <ChevronRightIcon />
                </button>
              </div>
            )}

            {currentFact && (
              <p className='fun-fact' key={currentFact}>
                {currentFact}
              </p>
            )}
          </div>

          {/* ── Change Box ── */}
          <div className={`change-box ${isRegenerating ? 'dimmed' : ''}`}>
            {/* Name display or edit */}
            {editingName ? (
              <div className='name-edit-row'>
                <input
                  className='name-input'
                  type='text'
                  value={nameInput}
                  maxLength={20}
                  onChange={(e) => setNameInput(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveNameLocally();
                    }
                    if (e.key === 'Escape') {
                      setEditingName(false);
                    }
                  }}
                />
                <button
                  className='save-name-btn'
                  onClick={handleSaveNameLocally}
                >
                  Save name
                </button>
              </div>
            ) : (
              <div className='name-display'>
                <span className='name-text'>{localNickname}</span>
                <button
                  className='edit-name-btn'
                  onClick={handleStartEditingName}
                >
                  <EditIcon />
                </button>
              </div>
            )}

            {/* Approaching limit warning */}
            {canChangeAppearance && appearanceChanges >= WARNING_THRESHOLD && (
              <div className='limit-warning-message'>
                <p>
                  You have{' '}
                  <strong>
                    {MAX_APPEARANCE_CHANGES - appearanceChanges} appearance{' '}
                    {MAX_APPEARANCE_CHANGES - appearanceChanges === 1
                      ? 'change'
                      : 'changes'}
                  </strong>{' '}
                  remaining
                </p>
              </div>
            )}

            {/* Max changes message */}
            {!canChangeAppearance && (
              <div className='max-changes-message'>
                <FaceWinkIcon />
                <p>
                  <strong>Ok perfectionist!</strong> {MAX_APPEARANCE_CHANGES}{' '}
                  changes is our limit, let's use the app
                </p>
              </div>
            )}

            {/* Appearance input */}
            <div className='appearance-input'>
              <textarea
                placeholder='Describe your changes'
                value={inputText}
                maxLength={500}
                onChange={(e) => setInputText(e.target.value)}
                disabled={!canChangeAppearance || isRegenerating}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitAppearance();
                  }
                }}
              />
              <div className='send-row'>
                <button
                  className='send-btn'
                  onClick={handleSubmitAppearance}
                  disabled={
                    !inputText.trim() || !canChangeAppearance || isRegenerating
                  }
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </div>

          {/* ── Bottom bar ── */}
          <div className={`bottom-bar ${isRegenerating ? 'dimmed' : ''}`}>
            <button className='cancel-edit-btn' onClick={handleEditCancel}>
              Cancel
            </button>
            <button
              className='save-assistant-btn'
              onClick={handleSaveAssistant}
              disabled={isRegenerating}
            >
              <span>Save Assistant</span>
              <ArrowCircleRightIcon />
            </button>
          </div>
        </div>

        {error && <p className='meet-error'>{error}</p>}
      </div>
    );
  }

  // ── Intro view (landing) ─────────────────────────────────────────────
  return (
    <div className='onboarding-step meet-assistant-step'>
      <div className='assistant-content'>
        {/* ── Image area ── */}
        <div className='image-area'>
          {currentImage ? (
            <div className='image-wrapper'>
              <DreamieTransition
                ref={transitionRef}
                imageUrl={currentImage.url}
                alt={nickname}
                onReformComplete={handleReformComplete}
                width={293}
                height={440}
              />
            </div>
          ) : (
            <div className='image-placeholder' />
          )}
        </div>

        {/* ── Chat area ── */}
        <div className='chat-area'>
          <div className='chat-messages'>
            {introMessages.map((msg, i) => (
              <div
                key={i}
                className='agent-bubble'
                style={{
                  borderColor: colors.border,
                  background: `linear-gradient(90deg, ${colors.from} 0%, ${colors.to} 100%)`,
                }}
              >
                {msg.text.split('\n').map((line, j) => (
                  <p key={j}>{line}</p>
                ))}
              </div>
            ))}

            {/* Action chips */}
            {phase === 'idle' && (
              <div className='action-chips'>
                <button className='action-chip' onClick={handleEnterEdit}>
                  <span>Change my name or appearance</span>
                  <FaceContentIcon />
                </button>
                <button
                  className='action-chip primary'
                  onClick={handleStartWorking}
                >
                  <span>Start working</span>
                  <ArrowCircleRightIcon />
                </button>
              </div>
            )}

            {phase === 'finishing' && (
              <div className='action-chips'>
                <button className='action-chip primary' disabled>
                  <span>Setting up…</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <p className='meet-error'>{error}</p>}
    </div>
  );
};

export default MeetAssistantStep;
